import { useRef, useCallback } from 'react';
import api from '../../services/api';

const useQuickMessages = () => {
  // Mantém referência de todos os controllers ativos
  const controllersRef = useRef([]);

  // Cria um controller e registra para cleanup
  const createController = () => {
    const controller = new AbortController();
    controllersRef.current.push(controller);
    return controller;
  };

  // Remove controller da lista após conclusão
  const removeController = (controller) => {
    controllersRef.current = controllersRef.current.filter(
      (c) => c !== controller,
    );
  };

  // Cancela todos os requests pendentes (chamar no useEffect cleanup)
  const cancelAll = useCallback(() => {
    controllersRef.current.forEach((c) => c.abort());
    controllersRef.current = [];
  }, []);

  const save = useCallback(async (data) => {
    const controller = createController();
    try {
      const { data: responseData } = await api.request({
        url: '/quick-messages',
        method: 'POST',
        data,
        signal: controller.signal,
      });
      return responseData;
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      throw err;
    } finally {
      removeController(controller);
    }
  }, []);

  const update = useCallback(async (data) => {
    const controller = createController();
    try {
      const { data: responseData } = await api.request({
        url: `/quick-messages/${data.id}`,
        method: 'PUT',
        data,
        signal: controller.signal,
      });
      return responseData;
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      throw err;
    } finally {
      removeController(controller);
    }
  }, []);

  const deleteRecord = useCallback(async (id) => {
    const controller = createController();
    try {
      const { data } = await api.request({
        url: `/quick-messages/${id}`,
        method: 'DELETE',
        signal: controller.signal,
      });
      return data;
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      throw err;
    } finally {
      removeController(controller);
    }
  }, []);

  const list = useCallback(async (params) => {
    const controller = createController();
    try {
      const { data } = await api.request({
        url: '/quick-messages/list',
        method: 'GET',
        params,
        signal: controller.signal,
      });
      return data;
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      throw err;
    } finally {
      removeController(controller);
    }
  }, []);

  return {
    save,
    update,
    deleteRecord,
    list,
    cancelAll,
  };
};

export default useQuickMessages;
